import { useEffect, useRef } from "react";
import { tidycalEmbedPath, tidycalBookingUrl } from "@/config/tidycal";
import { captureConsultationBookedAdsSignal, sendBookingAttributionBridge } from "@/lib/analytics";
import { useLang } from "@/i18n";

// Production-only real-booking gate (added 2026-10-09 after a live
// incident: this embed talks directly to the REAL TidyCal account —
// nothing backend-side can prevent a real external booking once this
// iframe is actually used). Fail-closed by ALLOWLIST, mirrors the same
// gate added to `frontend/public/index.html` for PostHog/GTM/OpenAI
// Ads — only the exact real production hostnames below ever load
// TidyCal's `embed.js` or register the `bookingComplete` listener.
const PRODUCTION_HOSTNAMES = ["interactglobally.com.br", "www.interactglobally.com.br"];
const isProductionHost = () => typeof window !== "undefined" && PRODUCTION_HOSTNAMES.includes(window.location.hostname);

/**
 * Reusable TidyCal embed — same `embed.js` script + `tidycal-embed` /
 * `data-path` convention the original marketing Booking page used, now
 * shared by every dedicated /book/* page, plus an ad-platform
 * conversion signal: TidyCal's embed posts a `bookingComplete` message
 * (origin `https://tidycal.com`) to the parent window once a visitor
 * finishes a real booking — see https://help.tidycal.com/article/141.
 * The CANONICAL PostHog `consultation_booked` business event comes from
 * the backend's own TidyCal reconciliation sweep, not from here — see
 * `captureConsultationBookedAdsSignal` in lib/analytics.js.
 *
 * When `service` has no configured booking type yet (`tidycalEmbedPath`
 * returns null), this renders a plain, honest "being set up" notice
 * instead of ever embedding another service's calendar or a fake one.
 *
 * `ctaNavContext` (bug fix, 2026-10-12) — the CTA-navigation context
 * (`cta_id`/`content_id`/`target_asset_id`/`run_id`) the hosting
 * dedicated booking page already consumed from `trackAssetViewed()`
 * at mount, held in state there (so the real value is re-rendered
 * into this prop, re-running this effect with a fresh closure, before
 * any real `bookingComplete` message could possibly arrive) and
 * passed straight through here so the explicit CTA that brought the
 * visitor to THIS booking page survives until `bookingComplete` and
 * reaches the bridge — see `sendBookingAttributionBridge()`'s own
 * docstring. `null` (default) for a direct/bookmarked visit with no
 * preceding CTA click, never fabricated.
 */
export const TidyCalEmbedWidget = ({ service, testId = "tidycal-embed", ctaNavContext = null }) => {
  const { t } = useLang();
  const dataPath = tidycalEmbedPath(service);
  const fallbackUrl = tidycalBookingUrl(service);
  const completedRef = useRef(false);
  const containerRef = useRef(null);
  const isProd = isProductionHost();

  useEffect(() => {
    if (!dataPath || !isProd) return; // non-production: never loads embed.js, never registers the bookingComplete listener — no real external booking can be created from this host.
    let cancelled = false;

    const onMessage = (e) => {
      if (e.origin !== "https://tidycal.com") return;
      if (!e.data || e.data.event !== "bookingComplete") return;
      if (completedRef.current) return;
      completedRef.current = true;
      const booking = e.data.data?.booking || {};
      captureConsultationBookedAdsSignal(service, {
        bookingTypeId: booking.booking_type_id,
        tidycalBookingId: booking.id,
        bookingTime: booking.starts_at,
      });
      // InterAct Tracking Foundation milestone (2026-10-10, pass 14) —
      // `booking.id` is the SAME raw TidyCal booking id the backend's
      // own reconciliation sweep stringifies into `provider_booking_id`
      // (consultation_booking_reconciliation.py) — the correlation key
      // the two independent signals meet on. Disabled by default
      // server-side; this call is always safe to make.
      sendBookingAttributionBridge(booking.id, ctaNavContext);
    };
    window.addEventListener("message", onMessage);

    // TidyCal's embed.js only auto-scans the DOM for `.tidycal-embed` divs
    // on its own initial (hard page load) execution — a div that appears
    // later via SPA client-side navigation is never picked up on its own
    // (confirmed live: 2nd/3rd/4th dedicated booking page visited in the
    // same session never rendered a calendar). `window.TidyCal.init(el)`
    // IS a real, callable re-init entry point (confirmed: calling it with
    // no argument throws "element not provided" — i.e. it expects exactly
    // the container element), so every mount explicitly re-triggers it
    // against THIS page's own container, instead of removing/re-adding
    // the script (verified live that does NOT make embed.js re-scan).
    const scriptSrc = "https://asset-tidycal.b-cdn.net/js/embed.js";
    const tryInit = () => {
      if (cancelled) return;
      if (window.TidyCal?.init && containerRef.current) {
        try {
          window.TidyCal.init(containerRef.current);
        } catch {
          /* no-op — next mount's retry loop below will try again */
        }
      } else {
        window.setTimeout(tryInit, 150);
      }
    };
    if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
      const s = document.createElement("script");
      s.src = scriptSrc;
      s.async = true;
      s.onload = tryInit;
      document.body.appendChild(s);
    } else {
      tryInit();
    }

    return () => {
      cancelled = true;
      window.removeEventListener("message", onMessage);
    };
  }, [dataPath, service, isProd, ctaNavContext]);

  if (!dataPath) {
    return (
      <div
        className="w-full min-h-[280px] bg-paper border border-ink/12 shadow-[6px_12px_44px_rgba(27,27,24,0.08)] p-6 md:p-8 flex flex-col items-center justify-center text-center gap-4"
        data-testid={`${testId}-missing`}
      >
        <p className="font-serif text-xl text-ink max-w-sm">{t("book.shared.fallback_missing_title")}</p>
        <p className="font-body text-ink-soft max-w-sm leading-relaxed">{t("book.shared.fallback_missing_body")}</p>
        <a
          href="mailto:christos@interactglobally.com.br"
          data-testid={`${testId}-missing-email`}
          className="mt-2 font-body text-sm text-terracotta underline decoration-terracotta/50 underline-offset-4 hover:text-ink transition-colors"
        >
          {t("book.shared.fallback_email_cta")}
        </a>
      </div>
    );
  }

  if (!isProd) {
    return (
      <div
        className="w-full min-h-[280px] bg-paper border border-ink/12 shadow-[6px_12px_44px_rgba(27,27,24,0.08)] p-6 md:p-8 flex flex-col items-center justify-center text-center gap-4"
        data-testid={`${testId}-preview-disabled`}
      >
        <p className="font-serif text-xl text-ink max-w-sm">Booking is disabled in this preview environment</p>
        <p className="font-body text-ink-soft max-w-sm leading-relaxed">
          This is a non-production deployment, so the live TidyCal calendar is never loaded here — no real booking can be created from this host.
        </p>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <div
        ref={containerRef}
        className="tidycal-embed w-full min-h-[720px] bg-paper border border-ink/12 shadow-[6px_12px_44px_rgba(27,27,24,0.08)] p-4 md:p-6"
        data-path={dataPath}
      />
      <p className="mt-3 text-center font-body text-xs text-ink-soft">
        <a href={fallbackUrl} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-fallback-link`} className="underline decoration-ink-soft/40 underline-offset-4 hover:text-terracotta">
          {t("book.shared.fallback_open_calendar")}
        </a>
      </p>
    </div>
  );
};

export default TidyCalEmbedWidget;
